import { useCallback, useEffect, useState } from "react";
import { getFileColumns } from "../../../../utils/parseFile";
import {
  getBestMatchSuggestions,
  getBOMColumnSuggestions,
  getPnPColumnSuggestions,
} from "./strategies/bestMatchFileSuggestions";
import {
  BOM_FILE_INFO_SCHEMA,
  FileSelectionStrategy,
  GERBER_FILE_LAYER_MAPPING_SCHEMA,
  PNP_FILE_INFO_SCHEMA,
} from "../index";
import { useFormikContext } from "formik";
import { UploadFormValues } from "../../UploadForm";
import { getPreviousUploadSuggestions } from "./strategies/previousUploadFileSuggestions";
import { getSuggestionsFromFileMappingFile } from "./strategies/includedFileMappingFileSuggestions";
import {
  Board,
  BOMFileInfo,
  PNPFileInfo,
  UploadConfig,
} from "../../../../reducers/uploadedBoard";
import { identifyFileTypesInGerberZip } from "./identifyGerberFileTypes";

export interface ZippedFile {
  path: string;
  content: Blob | string;
}

export interface ZippedFiles {
  allFiles: ZippedFile[];
  gerberFiles: ZippedFile[];
  drillFiles: ZippedFile[];
  fileMappingFile?: ZippedFile;
  extrepFile?: ZippedFile;
  bomOrPNPFiles: ZippedFile[];
  additionalFiles: ZippedFile[];
}

export function useReadGerberZip(
  file: Blob | undefined,
  fileSelectionStrategy: FileSelectionStrategy,
  selectedBoard: Board | null,
  fileSuggestionsDisabled: boolean,
) {
  const [zippedFiles, setZippedFiles] = useState<ZippedFiles | null>(null);
  const [bomFileColumns, setBOMFileColumns] = useState<string[]>([]);
  const [pnpFileColumns, setPNPFileColumns] = useState<string[]>([]);

  const [bestGuess, setBestGuess] = useState<UploadConfig | null>(null);
  const [previousUpload, setPreviousUpload] = useState<UploadConfig | null>(
    null,
  );
  const [includedFileMapping, setIncludedFileMapping] =
    useState<UploadConfig | null>(null);

  const { setFieldValue, setFieldTouched, values } =
    useFormikContext<UploadFormValues>();
  const getFileSuggestions = useCallback((): UploadConfig | null => {
    if (fileSelectionStrategy == FileSelectionStrategy.BEST_GUESS) {
      return bestGuess;
    } else if (fileSelectionStrategy == FileSelectionStrategy.PREVIOUS_UPLOAD) {
      return previousUpload;
    } else if (
      fileSelectionStrategy == FileSelectionStrategy.INCLUDED_FILE_MAPPING
    ) {
      return includedFileMapping;
    } else {
      throw Error("Unhandled strategy");
    }
  }, [bestGuess, fileSelectionStrategy, includedFileMapping, previousUpload]);

  const touchAllFieldsInObject = useCallback(
    async (
      fieldName: "gerberFileLayerMapping" | "bomFileInfo" | "pnpFileInfo",
      fields: string[],
    ) => {
      await setFieldTouched(fieldName, true, true);
      if (fields) {
        await Promise.all(
          fields.map(async (key) => {
            return await setFieldTouched(`${fieldName}.${key}`, true, true);
          }),
        );
      }
    },
    [setFieldTouched],
  );

  const updateFileSuggestions = useCallback(async () => {
    const fileSuggestions = getFileSuggestions();
    await setFieldValue(
      "gerberFileLayerMapping",
      fileSuggestions?.gerber_file_layer_mapping || null,
    );
    await setFieldValue("bomFileInfo", fileSuggestions?.bom_file_info || null);
    await setFieldValue("pnpFileInfo", fileSuggestions?.pnp_file_info || null);
    await setFieldValue(
      "assemblyDrawing",
      fileSuggestions?.assembly_drawing || null,
    );
    await setFieldValue("fabDrawing", fileSuggestions?.fab_drawing || null);
    await setFieldValue(
      "referenceFile",
      fileSuggestions?.reference_file || null,
    );

    // Touch the fields so that they are revalidated. Formik seems to mess this up otherwise
    await touchAllFieldsInObject(
      "gerberFileLayerMapping",
      Object.keys(GERBER_FILE_LAYER_MAPPING_SCHEMA.fields),
    );
    await touchAllFieldsInObject(
      "bomFileInfo",
      Object.keys(BOM_FILE_INFO_SCHEMA.fields),
    );
    await touchAllFieldsInObject(
      "pnpFileInfo",
      Object.keys(PNP_FILE_INFO_SCHEMA.fields),
    );
  }, [getFileSuggestions, setFieldValue, touchAllFieldsInObject]);

  useEffect(() => {
    if (!fileSuggestionsDisabled) {
      void updateFileSuggestions();
    }
  }, [updateFileSuggestions, fileSuggestionsDisabled]);

  const readColumns = useCallback(
    async (
      fileName: string | undefined,
      setColumns: (columns: string[]) => void,
      fileType: "bomFileInfo" | "pnpFileInfo",
      fieldValues: BOMFileInfo | PNPFileInfo | null,
    ) => {
      const file = zippedFiles?.bomOrPNPFiles.find(
        ({ path }) => path == fileName,
      );
      if (file !== undefined && fileName) {
        const columnInfo = await getFileColumns(
          file.path,
          file.content as Blob,
        );
        await setFieldValue(
          `${fileType}.header_row_index`,
          columnInfo?.headerRowIndex,
        );
        setColumns(columnInfo?.headers || []);
      } else {
        if (fieldValues) {
          await setFieldValue(fileType, null);
        }
        setColumns([]);
      }
      await touchAllFieldsInObject(
        fileType,
        fileType == "bomFileInfo"
          ? Object.keys(BOM_FILE_INFO_SCHEMA.fields)
          : Object.keys(PNP_FILE_INFO_SCHEMA.fields),
      );
    },
    [zippedFiles?.bomOrPNPFiles, touchAllFieldsInObject, setFieldValue],
  );

  useEffect(() => {
    void readColumns(
      values.bomFileInfo?.file_name,
      setBOMFileColumns,
      "bomFileInfo",
      values.bomFileInfo,
    );
  }, [readColumns, values.bomFileInfo]);

  useEffect(() => {
    void readColumns(
      values.pnpFileInfo?.file_name,
      setPNPFileColumns,
      "pnpFileInfo",
      values.pnpFileInfo,
    );
  }, [readColumns, values.pnpFileInfo]);

  useEffect(() => {
    const bomColumnSuggestions = getBOMColumnSuggestions(bomFileColumns);
    Object.entries(bomColumnSuggestions).map(([header, column]) => {
      if (
        values.bomFileInfo !== null &&
        !values.bomFileInfo[header as keyof BOMFileInfo]
      ) {
        void setFieldValue(`bomFileInfo.${header}`, column);
      }
    });
  }, [bomFileColumns, setFieldValue, values.bomFileInfo]);

  useEffect(() => {
    const pnpColumnSuggestions = getPnPColumnSuggestions(pnpFileColumns);
    Object.entries(pnpColumnSuggestions).map(([header, column]) => {
      if (
        values.pnpFileInfo !== null &&
        !values.pnpFileInfo[header as keyof PNPFileInfo]
      ) {
        void setFieldValue(`pnpFileInfo.${header}`, column);
      }
    });
  }, [pnpFileColumns, setFieldValue, values.pnpFileInfo]);

  const readGerberZipAndGetSuggestions = useCallback(async () => {
    if (file === undefined) {
      setZippedFiles(null);
    } else {
      const zippedFiles = await identifyFileTypesInGerberZip(file);
      setBestGuess(await getBestMatchSuggestions(zippedFiles));
      setPreviousUpload(
        await getPreviousUploadSuggestions(zippedFiles, selectedBoard),
      );
      setIncludedFileMapping(getSuggestionsFromFileMappingFile(zippedFiles));
      setZippedFiles(zippedFiles);
      await touchAllFieldsInObject(
        "gerberFileLayerMapping",
        Object.keys(GERBER_FILE_LAYER_MAPPING_SCHEMA.fields),
      );
      await touchAllFieldsInObject(
        "bomFileInfo",
        Object.keys(BOM_FILE_INFO_SCHEMA.fields),
      );
      await touchAllFieldsInObject(
        "pnpFileInfo",
        Object.keys(PNP_FILE_INFO_SCHEMA.fields),
      );
    }
  }, [file, selectedBoard, touchAllFieldsInObject]);

  useEffect(() => {
    void readGerberZipAndGetSuggestions();
  }, [readGerberZipAndGetSuggestions]);

  return { zippedFiles, bomFileColumns, pnpFileColumns };
}
